import axios from "axios";
import { useEffect, useState } from "react";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import DeleteConfirmationModal from "../DeleteConfirmationModal/DeleteConfirmationModal";
import LoaderSpiner from "../LoaderSpiner";
import styles from "./TemplateComponent.module.css";

const TemplateCard = ({
  template,
  title,
  description,
  useCases,
  columns,
  onEdit,
  onDelete,
}) => {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h2 className={styles.title}>
          {template.templateName} ({title})
        </h2>
      </div>
      <p className={styles.description}>{description}</p>
      <div className={styles.useCases}>
        <h3 className={styles.useCasesTitle}>AI Agent Use Cases:</h3>
        <ul className={styles.useCasesList}>
          {useCases.map((useCase, index) => (
            <li key={index}>{useCase}</li>
          ))}
        </ul>
      </div>
      <div className={styles.buttonContainer}>
        <button
          className={styles.editButton}
          onClick={() => onEdit(template.templateId)}
        >
          <FaEdit />
        </button>
        <button
          className={styles.deleteButton}
          onClick={() => onDelete(template.templateId)}
        >
          <MdDelete />
        </button>
      </div>
    </div>
  );
};

const ColumnSelectorModal = ({
  isOpen,
  onClose,
  onSave,
  onReset,
  title,
  columns,
  initialSelections,
  initialTemplateName,
  createdTemplates,
}) => {
  const [selectedColumns, setSelectedColumns] = useState(
    initialSelections || {}
  );
  const [templateName, setTemplateName] = useState(initialTemplateName || "");
  const [error, setError] = useState("");

  useEffect(() => {
    setTemplateName(initialTemplateName || "");
    setSelectedColumns(
      initialSelections ||
      (columns.length > 0
        ? columns.reduce((acc, column) => ({ ...acc, [column]: false }), {})
        : {})
    );
    setError("");
  }, [isOpen, initialSelections, initialTemplateName, columns]);

  const handleCheckboxChange = (column) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const handleTemplateNameChange = (e) => {
    setTemplateName(e.target.value);
    setError("");
  };

  const validateTemplateName = () => {
    if (!templateName.trim()) {
      setError("Template name is required.");
      return false;
    }
    if (
      createdTemplates.some(
        (t) =>
          t.templateName?.toLowerCase() ===
          templateName.trim()?.toLowerCase() &&
          t.templateName !== initialTemplateName
      )
    ) {
      setError("Template name must be unique.");
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validateTemplateName()) return;
    onSave(title, selectedColumns, templateName.trim());
    setTemplateName("");
    setSelectedColumns(
      columns.length > 0
        ? columns.reduce((acc, column) => ({ ...acc, [column]: false }), {})
        : {}
    );
  };

  const handleClose = () => {
    setTemplateName("");
    setSelectedColumns(
      columns.length > 0
        ? columns.reduce((acc, column) => ({ ...acc, [column]: false }), {})
        : {}
    );
    setError("");
    onReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <h2>
          {initialSelections ? "Edit Columns for" : "Select Columns for"}{" "}
          {title}
        </h2>
        <div className={styles.templateNameContainer}>
          <label className={styles.templateNameLabel}>
            Template Name (required):
            <input
              type="text"
              value={templateName}
              onChange={handleTemplateNameChange}
              className={styles.templateNameInput}
              placeholder="Enter unique template name"
            />
          </label>
          {error && <p className={styles.errorMessage}>{error}</p>}
        </div>
        {columns.length === 0 ? (
          <p>No columns available for this template.</p>
        ) : (
          <div className={styles.checkboxContainer}>
            {columns.map((column) => (
              <label key={column} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={selectedColumns[column] || false}
                  onChange={() => handleCheckboxChange(column)}
                />
                {column.charAt(0).toUpperCase() + column.slice(1)}
              </label>
            ))}
          </div>
        )}
        <div className={styles.modalButtons}>
          <button className={styles.cancelButton} onClick={handleClose}>
            Cancel
          </button>
          <button className={styles.saveButton} onClick={handleSave}>
            {initialSelections ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

const TemplateComponent = () => {
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [createdTemplates, setCreatedTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const API_BASE_URL =
    "https://prod.toxboxai.com/api/template";

  const getAuthHeaders = () => ({
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  const templates = [
    {
      title: "Dealership",
      description:
        "Manage car sales, customer inquiries, and service appointments for automotive dealerships.",
      useCases: [
        "Inbound: Answer inquiries about car availability, pricing, and test drives.",
        "Inbound: Schedule service appointments and provide status updates.",
        "Outbound: Follow up on lead for new car purchases or lease renewals.",
        "Outbound: Remind customers of upcoming service appointments or recalls.",
      ],
      columns: [
        "vehicleId",
        "model",
        "customerName",
        "appointmentDate",
        "status",
        "price",
        "vehicleType",
        "year",
        "mileage",
        "vin",
        "color",
        "location",
        "salesperson",
        "testDriveDate",
        "serviceHistory",
        "warranty",
        "financing",
        "discount",
        "deliveryStatus",
        "customerContact",
        "insuranceStatus",
        "registrationNumber",
        "lastServiceDate",
        "tradeInValue",
      ],
    },
    {
      title: "Loan",
      description:
        "Handle loan applications, payment reminders, and customer support for financial institutions.",
      useCases: [
        "Inbound: Assist with loan application status and eligibility questions.",
        "Inbound: Provide information on repayment options and terms.",
        "Outbound: Contact customers for payment reminders or overdue notices.",
        "Outbound: Upsell additional financial products like credit cards or insurance.",
      ],
      columns: [
        "loanId",
        "applicantName",
        "loanAmount",
        "applicationDate",
        "status",
        "interestRate",
        "creditScore",
        "loanType",
        "repaymentTerm",
        "collateral",
        "approvalDate",
        "disbursementDate",
        "lastPaymentDate",
        "remainingBalance",
        "loanOfficer",
        "applicationStatus",
        "paymentFrequency",
        "lateFees",
        "coApplicantName",
        "bankBranch",
        "loanPurpose",
        "creditHistory",
        "monthlyPayment",
      ],
    },
    {
      title: "Lead Generation",
      description:
        "Manage and qualify potential customers by assessing interest, capturing objections, and evaluating lead quality.",
      useCases: [
        "Inbound: Answer questions from interested prospects about products or services.",
        "Inbound: Qualify leads based on interest, urgency, and financial capability.",
        "Outbound: Follow up with website visitors or previous inquiries.",
        "Outbound: Gather additional information to improve lead scoring and segmentation.",
      ],
      columns: [
        "summary",
        "leadScore",
        "leadQuality",
        "showedPurchaseInterest",
        "objections",
        "sentiment",
        "urgency",
        "financialDetails",
        "timestamp",
        "interests",
        "error",
      ],
    },
    {
      title: "Hospital",
      description:
        "Streamline patient appointments, billing inquiries, and telehealth support for healthcare providers.",
      useCases: [
        "Inbound: Schedule or reschedule patient appointments and provide directions.",
        "Inbound: Answer billing questions and process payments.",
        "Outbound: Follow up on post-treatment care or patient satisfaction surveys.",
        "Outbound: Remind patients of upcoming appointments or prescription refills.",
      ],
      columns: [
        "patientId",
        "patientName",
        "appointmentDate",
        "department",
        "billingAmount",
        "status",
        "diagnosis",
        "doctorName",
        "insuranceProvider",
        "admissionDate",
        "dischargeDate",
        "roomNumber",
        "treatmentPlan",
        "billingStatus",
        "patientContact",
        "emergencyContact",
        "medication",
        "visitType",
        "labResults",
        "referralDoctor",
        "followUpDate",
        "patientAge",
        "medicalHistory",
      ],
    },
    {
      title: "School",
      description:
        "Support student enrollment, parent communication, and administrative tasks for educational institutions.",
      useCases: [
        "Inbound: Answer questions about enrollment processes or school events.",
        "Inbound: Provide information on tuition fees and payment plans.",
        "Outbound: Notify parents of school updates, events, or emergencies.",
        "Outbound: Follow up with prospective students or alumni for engagement.",
      ],
      columns: [
        "studentId",
        "studentName",
        "enrollmentDate",
        "grade",
        "tuitionStatus",
        "event",
        "parentName",
        "subject",
        "teacherName",
        "schoolYear",
        "attendance",
        "gpa",
        "extracurricular",
        "scholarship",
        "parentContact",
        "enrollmentStatus",
        "classroom",
        "studentContact",
        "disciplinaryRecord",
        "busRoute",
        "libraryCard",
        "emergencyContact",
        "healthRecord",
      ],
    },
    {
      title: "Retail",
      description:
        "Manage customer inquiries, order tracking, and promotions for retail businesses.",
      useCases: [
        "Inbound: Answer questions about product availability, pricing, or store locations.",
        "Inbound: Assist with order tracking, returns, refunds, or exchanges.",
        "Outbound: Follow up on abandoned carts to encourage purchase completion.",
        "Outbound: Promote seasonal sales, new product launches, or limited-time offers.",
      ],
      columns: [
        "orderId",
        "customerName",
        "product",
        "orderDate",
        "totalAmount",
        "status",
        "storeLocation",
        "category",
        "sku",
        "quantity",
        "discountCode",
        "paymentMethod",
        "shippingAddress",
        "orderStatus",
        "returnStatus",
        "clerkName",
        "customerFeedback",
        "promotion",
        "inventoryStatus",
        "customerContact",
        "loyaltyPoints",
        "warrantyPeriod",
        "deliveryDate",
      ],
    },
    {
      title: "Real Estate Lead",
      description:
        "Capture and manage leads for real estate inquiries, including customer interest, questions, and engagement details.",
      useCases: [
        "Inbound: Capture customer interest and questions about properties or projects.",
        "Inbound: Schedule site visits, virtual tours, or consultations with agents.",
        "Outbound: Follow up with leads to provide property details or confirm interest.",
        "Outbound: Promote new projects, special offers, or financing options.",
      ],
      columns: [
        "agent_Name",
        "customer_Name",
        "interest",
        "questions",
        "ad_engagemen",
        "budget",
        "preferred_location",
        "property_type",
        "timeline",
        "contact_info",
        "specific_needs",
      ],
    },
    {
      title: "Hospitality",
      description:
        "Manage reservations, customer inquiries, and loyalty programs for hotels and restaurants.",
      useCases: [
        "Inbound: Handle booking requests for rooms, tables, or event spaces.",
        "Inbound: Answer questions about amenities, menus, or cancellation policies.",
        "Outbound: Follow up with guests to confirm reservations or offer upgrades.",
        "Outbound: Promote seasonal packages, dining specials, or loyalty benefits.",
      ],
      columns: [
        "reservationId",
        "guestName",
        "checkInDate",
        "roomType",
        "totalCost",
        "status",
        "checkOutDate",
        "numberOfGuests",
        "bookingSource",
        "specialRequests",
        "roomRate",
        "loyaltyProgram",
        "staffName",
        "paymentStatus",
        "guestContact",
        "amenities",
        "reservationStatus",
        "roomNumber",
        "diningPreferences",
        "eventBooking",
        "parking",
        "guestFeedback",
        "cleaningStatus",
      ],
    },
    {
      title: "Travel and Tourism",
      description:
        "Handle bookings, itinerary inquiries, and customer support for travel agencies and airlines.",
      useCases: [
        "Inbound: Assist with booking flights, hotels, or tour packages.",
        "Inbound: Answer questions about travel restrictions, refunds, or cancellations.",
        "Outbound: Follow up on incomplete bookings or offer travel deals.",
        "Outbound: Remind customers of upcoming trips or check-in requirements.",
      ],
      columns: [
        "bookingId",
        "travelerName",
        "destination",
        "travelDate",
        "cost",
        "status",
        "departureDate",
        "returnDate",
        "travelType",
        "bookingStatus",
        "agentContact",
        "itinerary",
        "travelInsurance",
        "flightNumber",
        "hotelName",
        "visaStatus",
        "travelerContact",
        "tourGuide",
        "luggageAllowance",
        "emergencyContact",
        "paymentStatus",
        "travelPackage",
        "seatPreference",
      ],
    },
    {
      title: "Utilities",
      description:
        "Manage billing inquiries, service requests, and outage updates for utility providers.",
      useCases: [
        "Inbound: Answer questions about billing, payment plans, or service disruptions.",
        "Inbound: Schedule technician visits for installations or repairs.",
        "Outbound: Notify customers of planned outages or service upgrades.",
        "Outbound: Remind customers of overdue bills or offer energy-saving plans.",
      ],
      columns: [
        "accountId",
        "customerName",
        "billingDate",
        "amountDue",
        "serviceType",
        "status",
        "meterNumber",
        "usage",
        "serviceAddress",
        "billingCycle",
        "technicianName",
        "outageStatus",
        "contractTerm",
        "accountStatus",
        "paymentDate",
        "servicePlan",
        "customerType",
        "installationDate",
        "meterReading",
        "emergencyContact",
        "billingStatus",
        "energySource",
        "serviceRequest",
      ],
    },
    {
      title: "Telecommunications",
      description:
        "Handle plan inquiries, technical support, and promotions for telecom providers.",
      useCases: [
        "Inbound: Assist with plan changes, billing queries, or technical issues.",
        "Inbound: Provide information on new plans, devices, or promotions.",
        "Outbound: Follow up on customer inquiries or upsell premium plans.",
        "Outbound: Remind customers of contract renewals or payment deadlines.",
      ],
      columns: [
        "accountId",
        "customerName",
        "planType",
        "billingDate",
        "amount",
        "status",
        "deviceType",
        "contractStart",
        "contractEnd",
        "dataUsage",
        "supportAgent",
        "serviceStatus",
        "upgradeEligibility",
        "imei",
        "phoneNumber",
        "billingStatus",
        "customerContact",
        "networkType",
        "installationDate",
        "promotionCode",
        "accountType",
        "serviceArea",
        "dataLimit",
      ],
    },
    {
      title: "Customer Support",
      description:
        "Provide technical support, product assistance, and issue resolution for various industries.",
      useCases: [
        "Inbound: Troubleshoot technical issues with products or services.",
        "Inbound: Answer questions about product features, warranties, or returns.",
        "Outbound: Follow up on open support tickets or unresolved issues.",
        "Outbound: Conduct satisfaction surveys after support interactions.",
      ],
      columns: [
        "ticketId",
        "customerName",
        "issueType",
        "openDate",
        "priority",
        "status",
        "resolutionDate",
        "agentName",
        "ticketStatus",
        "productName",
        "warrantyStatus",
        "escalationLevel",
        "customerSatisfaction",
        "ticketSource",
        "followUpDate",
        "issueDescription",
        "customerContact",
        "supportChannel",
        "resolutionDetails",
        "ticketCategory",
        "slaCompliance",
        "feedbackScore",
        "attachment",
      ],
    },
    {
      title: "Non-Profit",
      description:
        "Support fundraising, volunteer coordination, and outreach for non-profit organizations.",
      useCases: [
        "Inbound: Answer questions about donation processes or volunteer opportunities.",
        "Inbound: Provide information on campaigns, events, or tax benefits.",
        "Outbound: Contact donors to thank them or encourage recurring contributions.",
        "Outbound: Promote fundraising events or community initiatives.",
      ],
      columns: [
        "donorId",
        "donorName",
        "donationAmount",
        "donationDate",
        "campaign",
        "status",
        "volunteerName",
        "eventName",
        "donationType",
        "taxReceipt",
        "donorContact",
        "volunteerRole",
        "fundUsage",
        "campaignGoal",
        "donationStatus",
        "eventDate",
        "volunteerContact",
        "programName",
        "sponsorName",
        "impactMetric",
        "grantStatus",
        "outreachChannel",
        "recurringDonation",
      ],
    },
    {
      title: "Legal Services",
      description:
        "Manage client inquiries, appointment scheduling, and case updates for law firms.",
      useCases: [
        "Inbound: Schedule consultations or answer questions about legal services.",
        "Inbound: Provide information on case requirements or billing.",
        "Outbound: Follow up on potential clients or case progress updates.",
        "Outbound: Remind clients of court dates or document submission deadlines.",
      ],
      columns: [
        "caseId",
        "clientName",
        "caseType",
        "filingDate",
        "fees",
        "status",
        "attorneyName",
        "courtDate",
        "clientContact",
        "caseStatus",
        "legalDocument",
        "billingHours",
        "opposingParty",
        "casePriority",
        "consultationDate",
        "settlementAmount",
        "caseNumber",
        "evidenceStatus",
        "clientFeedback",
        "referralSource",
        "caseOutcome",
        "documentCount",
        "hearingDate",
      ],
    },
    {
      title: "Fitness and Wellness",
      description:
        "Handle class bookings, membership inquiries, and promotions for gyms and spas.",
      useCases: [
        "Inbound: Schedule fitness classes, spa treatments, or personal training sessions.",
        "Inbound: Answer questions about membership plans or facility amenities.",
        "Outbound: Follow up on trial members to encourage full membership.",
        "Outbound: Promote wellness events, new classes, or special offers.",
      ],
      columns: [
        "memberId",
        "memberName",
        "membershipType",
        "startDate",
        "fees",
        "status",
        "trainerName",
        "className",
        "membershipStatus",
        "classSchedule",
        "facility",
        "memberContact",
        "healthGoal",
        "sessionCount",
        "promotionCode",
        "paymentStatus",
        "attendanceRecord",
        "equipmentUsed",
        "injuryStatus",
        "referralProgram",
        "classCapacity",
        "wellnessScore",
        "bookingSource",
      ],
    },
    {
      title: "Event Management",
      description:
        "Manage event bookings, attendee inquiries, and promotions for events like weddings or conferences.",
      useCases: [
        "Inbound: Answer questions about event details, pricing, or availability.",
        "Inbound: Assist with ticket purchases or RSVP confirmations.",
        "Outbound: Follow up with attendees for event confirmations or updates.",
        "Outbound: Promote upcoming events or early-bird discounts.",
      ],
      columns: [
        "eventId",
        "attendeeName",
        "eventDate",
        "eventType",
        "ticketPrice",
        "status",
        "venueName",
        "eventStatus",
        "ticketType",
        "organizerName",
        "attendeeContact",
        "sponsorName",
        "eventBudget",
        "registrationStatus",
        "eventDuration",
        "catering",
        "attendeeCount",
        "marketingChannel",
        "equipmentSetup",
        "eventFeedback",
        "securityPlan",
        "ticketSales",
        "vendorContact",
      ],
    },
    {
      title: "Home Services",
      description:
        "Handle service requests, scheduling, and customer inquiries for plumbing, cleaning, or HVAC.",
      useCases: [
        "Inbound: Schedule service appointments for repairs or maintenance.",
        "Inbound: Answer questions about pricing, warranties, or service areas.",
        "Outbound: Follow up on completed services for feedback or upselling.",
        "Outbound: Remind customers of seasonal maintenance or promotions.",
      ],
      columns: [
        "serviceId",
        "customerName",
        "serviceType",
        "appointmentDate",
        "cost",
        "status",
        "technicianName",
        "serviceStatus",
        "serviceAddress",
        "warrantyPeriod",
        "customerContact",
        "serviceCategory",
        "equipment",
        "serviceICAS",
        "invoiceNumber",
        "paymentStatus",
        "customerFeedback",
        "serviceArea",
        "partsUsed",
        "followUpDate",
        "contractStatus",
        "urgencyLevel",
        "serviceNotes",
      ],
    },
    {
      title: "Logistics and Delivery",
      description:
        "Manage shipment tracking, delivery scheduling, and customer support for courier services.",
      useCases: [
        "Inbound: Assist with tracking shipments or resolving delivery issues.",
        "Inbound: Schedule pickups or answer questions about delivery options.",
        "Outbound: Notify customers of delivery status or delays.",
        "Outbound: Promote premium delivery services or loyalty programs.",
      ],
      columns: [
        "shipmentId",
        "customerName",
        "deliveryDate",
        "destination",
        "cost",
        "status",
        "trackingNumber",
        "carrierName",
        "deliveryStatus",
        "pickupDate",
        "customerContact",
        "packageWeight",
        "deliveryAddress",
        "deliveryType",
        "driverName",
        "deliveryNotes",
        "insuranceStatus",
        "shipmentType",
        "warehouseLocation",
        "deliveryAttempt",
        "signatureStatus",
        "packageDimensions",
        "customsStatus",
      ],
    },
    {
      title: "Government Services",
      description:
        "Provide public helpline support, appointment scheduling, and information dissemination.",
      useCases: [
        "Inbound: Answer questions about public services, permits, or tax assistance.",
        "Inbound: Schedule appointments for government offices or hearings.",
        "Outbound: Notify citizens of policy changes or public health updates.",
        "Outbound: Conduct surveys on public service satisfaction.",
      ],
      columns: [
        "requestId",
        "citizenName",
        "serviceType",
        "requestDate",
        "status",
        "priority",
        "departmentName",
        "requestStatus",
        "citizenContact",
        "serviceCategory",
        "caseWorker",
        "requestPriority",
        "submissionDate",
        "resolutionDate",
        "serviceLocation",
        "documentNumber",
        "appointmentDate",
        "feedbackScore",
        "complianceStatus",
        "serviceOutcome",
        "referralAgency",
        "caseNotes",
        "publicNotice",
      ],
    },
    {
      title: "Entertainment",
      description:
        "Handle ticketing, subscription inquiries, and promotions for movies, concerts, or streaming.",
      useCases: [
        "Inbound: Assist with ticket purchases or subscription plan changes.",
        "Inbound: Answer questions about showtimes, venues, or refund policies.",
        "Outbound: Promote upcoming events, movies, or subscription renewals.",
        "Outbound: Follow up on customer feedback or abandoned ticket purchases.",
      ],
      columns: [
        "ticketId",
        "customerName",
        "eventDate",
        "eventType",
        "ticketPrice",
        "status",
        "showTime",
        "venueName",
        "ticketStatus",
        "customerContact",
        "eventName",
        "seatNumber",
        "ticketCategory",
        "promotionCode",
        "eventStatus",
        "organizerContact",
        "refundStatus",
        "entryTime",
        "ticketSource",
        "customerFeedback",
        "eventDuration",
        "streamingAccess",
        "merchandiseSales",
      ],
    },
    {
      title: "Manufacturing",
      description:
        "Manage equipment sales, maintenance scheduling, and customer support for manufacturing firms.",
      useCases: [
        "Inbound: Answer inquiries about equipment specifications or pricing.",
        "Inbound: Schedule maintenance or repair services for equipment.",
        "Outbound: Follow up on leads for equipment sales or service contracts.",
        "Outbound: Remind customers of maintenance schedules or warranty renewals.",
      ],
      columns: [
        "equipmentId",
        "clientName",
        "equipmentType",
        "purchaseDate",
        "cost",
        "status",
        "serialNumber",
        "productionDate",
        "equipmentStatus",
        "clientContact",
        "warrantyPeriod",
        "maintenanceSchedule",
        "productionLine",
        "unitCost",
        "orderQuantity",
        "deliveryDate",
        "installationDate",
        "operatorName",
        "safetyCompliance",
        "downtimeRecord",
        "spareParts",
        "serviceContract",
        "equipmentLocation",
      ],
    },
  ];

  // Fetch templates from API on component mount
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(API_BASE_URL, getAuthHeaders());
        // Rename _id to templateId in fetched templates
        const templatesWithTemplateId = response.data.map((template) => ({
          ...template,
          templateId: template._id,
          _id: undefined,
        }));
        setCreatedTemplates(templatesWithTemplateId);
      } catch (err) {
        setError("Failed to fetch templates. Please try again.");
        console.error("Error fetching templates:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const handleTemplateChange = (e) => {
    setSelectedTemplate(e.target.value);
  };

  const handleSelectColumns = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (id) => {
    const templateToEdit = createdTemplates.find((t) => t.templateId === id);
    setEditingTemplate(templateToEdit);
    setSelectedTemplate(templateToEdit.title);
    setIsModalOpen(true);
  };

  const handleDeleteTemplate = (id) => {
    const template = createdTemplates.find((t) => t.templateId === id);
    setTemplateToDelete(template);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    setLoading(true);
    setError("");
    try {
      await axios.delete(
        `${API_BASE_URL}/${templateToDelete.templateId}`,
        getAuthHeaders()
      );
      setCreatedTemplates((prev) =>
        prev.filter((t) => t.templateId !== templateToDelete.templateId)
      );
    } catch (err) {
      setError("Failed to delete template. Please try again.");
      console.error("Error deleting template:", err);
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleSaveTemplate = async (title, selectedColumns, templateName) => {
    const template = templates.find((t) => t.title === title);
    const activeColumns = template.columns.filter(
      (col) => selectedColumns[col]
    );

    const templateData = {
      templateName,
      title: template.title,
      description: template.description,
      useCases: template.useCases,
      columns: activeColumns,
    };

    setLoading(true);
    setError("");
    try {
      if (editingTemplate) {
        // Update existing template (PUT)
        const response = await axios.put(
          `${API_BASE_URL}/${editingTemplate.templateId}`,
          templateData,
          getAuthHeaders()
        );
        setCreatedTemplates((prev) =>
          prev.map((t) =>
            t.templateId === editingTemplate.templateId
              ? {
                ...response.data,
                templateId: response.data._id,
                _id: undefined,
              }
              : t
          )
        );
      } else {
        // Create new template (POST)
        const response = await axios.post(
          API_BASE_URL,
          templateData,
          getAuthHeaders()
        );
        setCreatedTemplates((prev) => [
          ...prev,
          { ...response.data, templateId: response.data._id, _id: undefined },
        ]);
      }
      console.log(
        `${editingTemplate ? "Updated" : "Created"} template JSON:`,
        JSON.stringify(templateData, null, 2)
      );
    } catch (err) {
      setError(
        `Failed to ${editingTemplate ? "update" : "create"
        } template. Please try again.`
      );
      console.error(
        `Error ${editingTemplate ? "updating" : "creating"} template:`,
        err
      );
    } finally {
      setLoading(false);
      setSelectedTemplate("");
      setEditingTemplate(null);
      setIsModalOpen(false);
    }
  };

  const handleReset = () => {
    setSelectedTemplate("");
    setEditingTemplate(null);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const filteredTemplates = createdTemplates.filter(
    (template) =>
      template.templateName
        ?.toLowerCase()
        .includes(debouncedQuery?.toLowerCase()) ||
      template.title?.toLowerCase().includes(debouncedQuery?.toLowerCase()) ||
      template.description
        ?.toLowerCase()
        .includes(debouncedQuery?.toLowerCase())
  );

  return (
    <div className={styles.container}>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search created templates..."
          value={searchQuery}
          onChange={handleSearch}
          className={styles.searchInput}
        />
        <select
          value={selectedTemplate}
          onChange={handleTemplateChange}
          className={styles.templateDropdown}
        >
          <option value="">Select a template</option>
          {templates.map((template) => (
            <option key={template.title} value={template.title}>
              {template.title}
            </option>
          ))}
        </select>
        {selectedTemplate && (
          <button
            className={styles.selectColumnsButton}
            onClick={handleSelectColumns}
          >
            Select Columns
          </button>
        )}
      </div>
      {loading ? (
        <LoaderSpiner />
      ) : (
        <div className={styles.grid}>
          {filteredTemplates.length > 0 ? (
            filteredTemplates.map((template, index) => (
              <TemplateCard
                key={template.templateId}
                template={template}
                title={template.title}
                description={template.description}
                useCases={template.useCases}
                columns={template.columns}
                onEdit={handleEditTemplate}
                onDelete={handleDeleteTemplate}
              />
            ))
          ) : (
            <p>No templates created yet or no matches found.</p>
          )}
        </div>
      )}

      <ColumnSelectorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onReset={handleReset}
        onSave={handleSaveTemplate}
        title={selectedTemplate}
        columns={
          templates.find((t) => t.title === selectedTemplate)?.columns || []
        }
        initialSelections={
          editingTemplate
            ? editingTemplate.columns.reduce(
              (acc, col) => ({ ...acc, [col]: true }),
              {}
            )
            : null
        }
        initialTemplateName={
          editingTemplate ? editingTemplate.templateName : ""
        }
        createdTemplates={createdTemplates}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setTemplateToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        templateName={templateToDelete?.templateName || ""}
      />
    </div>
  );
};

export default TemplateComponent;
